import {
  organizationInteractionEventTypes,
  submissionDomainEventTypes,
  submissionInteractionEventTypes
} from '../actions/v1/events';
import {
  faEnvelope,
  faFilePen,
  faUserCheck,
  faUserXmark,
  faFileCircleExclamation,
  faClock,
  faFileArrowUp
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { IPeerReviewWithCaseDataDb } from '@publishing-engine/submission-service/src/db/repositories/PeerReview';
import { Primitive } from '../types';
import { IRoleType } from './workflow-phase-ownership';

export type IEmailEnvelopeData = {
  subject?: string;
  body?: string;
  recipients?: {
    to?: string[];
    cc?: string[];
    bcc?: string[];
  };
  attachments?: Array<{
    name: string;
    url: string;
    size?: number;
  }>;
};

export type IActivityLog = {
  submissionId: string;
  submissionContextId: string;
  type: string;
  date: string;
  userId: string;
  userFirstName: string;
  userLastName: string;
  userStatus?: 'invited' | 'active' | 'deactivated';
  reviewerFirstName?: string;
  reviewerLastName?: string;
  reviewerStatus?: 'invited' | 'active' | 'deactivated';
  reviewerEmail?: string | IEmailEnvelopeData;
  assignee?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    status?: 'invited' | 'active' | 'deactivated';
  };
  expertEmail?: string | IEmailEnvelopeData;
  editorialRequest?: {
    comment?: string;
    attachments?: Array<{
      name: string;
      url: string;
      size?: number;
    }>;
  };
  phase?: string;
  content?: string;
  action?: string;
  eventId?: string;
  manuscriptId?: string;
  autoAssign?: boolean;
  isOwner?: boolean;
  // TODO: Revisit how we're storing data on events, since we don't have a clear pattern for that
  changes?: Array<Record<string, unknown>>;
  revisionSeverity?: string;
  count?: number;
  fields?: string[];
  queue?: Array<{
    email: string;
    firstName: string;
    lastName: string;
    cc_recipient: Array<unknown>;
    attachments: Array<unknown>;
  }>;
  active?: boolean;
  tagDeleteList?: string[];
  tagAddList?: string[];
  editorComment?: string;
  proxyUserId?: string;
  proxyUserFirstName?: string;
  proxyUserLastName?: string;
  proxyUserStatus?: 'invited' | 'active' | 'deactivated';
  closedReviews?: IPeerReviewWithCaseDataDb[];
  eventPayload?: Record<string, Primitive>;
  attachments?: Array<{
    name: string;
    url: string;
    size?: number;
  }>;
  status?: string;
  exportConfigType?: string;
  isAdminChecklistDone?: boolean;
  role?: string;
  isSystem?: boolean;
  reason?: string;
};

export type allActivityLogTypes =
  | (typeof organizationInteractionEventTypes)[number]
  | (typeof submissionInteractionEventTypes)[number]
  | (typeof submissionDomainEventTypes)[number];

export type EnsureExhaustiveActivityLogTypes<
  T extends Record<allActivityLogTypes, any>
> = T;

export const ACTIVITY_TYPE_INFO: EnsureExhaustiveActivityLogTypes<
  Record<
    allActivityLogTypes,
    { type: allActivityLogTypes; label: string; icon: IconDefinition }
  >
> = {
  // Submission
  SubmissionCreated: {
    type: 'SubmissionCreated',
    label: 'Submission Created',
    icon: faFileCircleExclamation
  },
  SubmissionCloned: {
    type: 'SubmissionCloned',
    label: 'Submission Cloned',
    icon: faFileCircleExclamation
  },
  SubmissionNote: {
    type: 'SubmissionNote',
    label: 'Submission Note',
    icon: faFilePen
  },
  SubmissionEdited: {
    type: 'SubmissionEdited',
    label: 'Submission Edited',
    icon: faFilePen
  },
  NotifyAuthorOnManuscriptUpdate: {
    type: 'NotifyAuthorOnManuscriptUpdate',
    label: 'Notify Author on Manuscript Update',
    icon: faFilePen
  },
  SubmissionAssigned: {
    type: 'SubmissionAssigned',
    label: 'Submission Assigned',
    icon: faUserCheck
  },
  SubmissionUnassigned: {
    type: 'SubmissionUnassigned',
    label: 'Submission Unassigned',
    icon: faUserXmark
  },
  EditorialSubmissionDecision: {
    type: 'EditorialSubmissionDecision',
    label: 'Editorial Decision',
    icon: faFileCircleExclamation
  },
  SubmissionApprovedForPreProduction: {
    type: 'SubmissionApprovedForPreProduction',
    label: 'Submission In Pre-Production',
    icon: faFileCircleExclamation
  },
  SubmissionDeadlinePassed: {
    type: 'SubmissionDeadlinePassed',
    label: 'Submission Deadline Passed',
    icon: faClock
  },
  EarlyAssessmentReminderTriggered: {
    type: 'EarlyAssessmentReminderTriggered',
    label: 'Early Assessment Reminder Triggered',
    icon: faClock
  },
  AdjudicationReminderTriggered: {
    type: 'AdjudicationReminderTriggered',
    label: 'Adjudication Reminder Triggered',
    icon: faClock
  },
  ReviewReminderTriggered: {
    type: 'ReviewReminderTriggered',
    label: 'Review Reminder Triggered',
    icon: faClock
  },
  SubmissionReminderTriggered: {
    type: 'SubmissionReminderTriggered',
    label: 'Submission Reminder Triggered',
    icon: faClock
  },
  LicensingSubmitted: {
    type: 'LicensingSubmitted',
    label: 'Licensing Submitted',
    icon: faFileCircleExclamation
  },
  EditorialSubmissionRejected: {
    type: 'EditorialSubmissionRejected',
    label: 'Submission Rejected',
    icon: faFileCircleExclamation
  },
  AuthorChangeRequestCompleted: {
    type: 'AuthorChangeRequestCompleted',
    label: 'Change Request Completed by Author',
    icon: faFileCircleExclamation
  },
  IntegrationResult: {
    type: 'IntegrationResult',
    label: 'Integration Result',
    icon: faFileCircleExclamation
  },
  EditorialChangeRequestSubmitted: {
    type: 'EditorialChangeRequestSubmitted',
    label: 'Change Request Submitted to Author',
    icon: faFileCircleExclamation
  },
  EditorialSubmissionUnsubmitted: {
    type: 'EditorialSubmissionUnsubmitted',
    label: 'Submission Unsubmitted',
    icon: faFileCircleExclamation
  },

  // Initial Assessment
  EditorialFormDataCreated: {
    type: 'EditorialFormDataCreated',
    label: 'Initial Assessment Checklist',
    icon: faFilePen
  },

  // Review Phase & Revision Phase
  ReviewRequested: {
    type: 'ReviewRequested',
    label: 'Peer Review Requested',
    icon: faEnvelope
  },
  DeclinedReviewInvitationResent: {
    type: 'DeclinedReviewInvitationResent',
    label: 'Peer Review Declined Invitation Resent',
    icon: faUserCheck
  },
  ExpiredReviewInvitationResent: {
    type: 'ExpiredReviewInvitationResent',
    label: 'Peer Review Expired Invitation Resent',
    icon: faUserCheck
  },
  WithdrawnReviewInvitationResent: {
    type: 'WithdrawnReviewInvitationResent',
    label: 'Peer Review Withdrawn Invitation Resent',
    icon: faUserCheck
  },
  PeerReviewDecision: {
    type: 'PeerReviewDecision',
    label: 'Peer Review Decision',
    icon: faFileCircleExclamation
  },
  CoReviewDecision: {
    type: 'CoReviewDecision',
    label: 'Co-Reviewer Decision',
    icon: faFileCircleExclamation
  },
  FirstAssessmentSubmitted: {
    type: 'FirstAssessmentSubmitted',
    label: 'First Assessment Submitted',
    icon: faFileCircleExclamation
  },
  ReviewRequestEnqueued: {
    type: 'ReviewRequestEnqueued',
    label: 'Reviewers Added to Queue',
    icon: faUserCheck
  },
  EnqueuedReviewerRemoved: {
    type: 'EnqueuedReviewerRemoved',
    label: 'Reviewers Removed from Queue',
    icon: faUserXmark
  },
  QueueToggled: {
    type: 'QueueToggled',
    label: 'Peer Review Queue Toggled',
    icon: faFileCircleExclamation
  },
  MinReviewCountSet: {
    type: 'MinReviewCountSet',
    label: 'Recommended Number of Reviews',
    icon: faFileCircleExclamation
  },
  ReviewRated: {
    type: 'ReviewRated',
    label: 'Peer Review Rated',
    icon: faFileCircleExclamation
  },
  RescindedFirstAssessment: {
    type: 'RescindedFirstAssessment',
    label: 'Rescinded Reviews',
    icon: faFileCircleExclamation
  },
  PeerReviewModerated: {
    type: 'PeerReviewModerated',
    label: 'Peer Review Moderated',
    icon: faFilePen
  },
  ReviewDeadlineExtensionRequested: {
    type: 'ReviewDeadlineExtensionRequested',
    label: 'Review Deadline Extension Requested',
    icon: faClock
  },
  ReviewDeadlineExtended: {
    type: 'ReviewDeadlineExtended',
    label: 'Review Deadline Extended',
    icon: faClock
  },
  ReviewInRevisionRequested: {
    type: 'ReviewInRevisionRequested',
    label: 'Review In Revision Requested',
    icon: faFileCircleExclamation
  },
  ReviewRemoved: {
    type: 'ReviewRemoved',
    label: 'Review Removed',
    icon: faUserXmark
  },
  ReviewDeadlinePassed: {
    type: 'ReviewDeadlinePassed',
    label: 'Review Deadline Passed',
    icon: faClock
  },
  ReviewTargetMetEditorNotification: {
    type: 'ReviewTargetMetEditorNotification',
    label: 'Review Target Met Editor Notification',
    icon: faFileCircleExclamation
  },
  RevisionRequested: {
    type: 'RevisionRequested',
    label: 'Revision Requested',
    icon: faFileCircleExclamation
  },
  RevisionSubmitted: {
    type: 'RevisionSubmitted',
    label: 'Revision Submitted',
    icon: faFilePen
  },
  RevisionReviewRequested: {
    type: 'RevisionReviewRequested',
    label: 'Revision Review Requested',
    icon: faFileCircleExclamation
  },
  RevisionAssessmentSubmitted: {
    type: 'RevisionAssessmentSubmitted',
    label: 'Revision Assessment Submitted',
    icon: faFileCircleExclamation
  },
  EditorialInvitedReviewInvitationWithdrawn: {
    type: 'EditorialInvitedReviewInvitationWithdrawn',
    label: 'Review Invitation Withdrawn',
    icon: faUserXmark
  },
  EditorialAcceptedReviewInvitationWithdrawn: {
    type: 'EditorialAcceptedReviewInvitationWithdrawn',
    label: 'Accepted Review Invitation Withdrawn',
    icon: faUserXmark
  },
  EditorialAwaitingRevisionReviewInvitationWithdrawn: {
    type: 'EditorialAwaitingRevisionReviewInvitationWithdrawn',
    label: 'Awaiting Revision Review Invitation Withdrawn',
    icon: faUserXmark
  },
  EditorialRevisionInvitedReviewInvitationWithdrawn: {
    type: 'EditorialRevisionInvitedReviewInvitationWithdrawn',
    label: 'Revision Review Invitation Withdrawn',
    icon: faUserXmark
  },
  EditorialRevisionAssessmentReviewInvitationWithdrawn: {
    type: 'EditorialRevisionAssessmentReviewInvitationWithdrawn',
    label: 'Revision Assessment Review Invitaion Withrdawn',
    icon: faUserXmark
  },

  // Submission Deadline Extension
  SubmissionDeadlineExtensionRequested: {
    type: 'SubmissionDeadlineExtensionRequested',
    label: 'Submission Deadline Extension Requested',
    icon: faClock
  },
  SubmissionDeadlineExtended: {
    type: 'SubmissionDeadlineExtended',
    label: 'Submission Deadline Extended',
    icon: faClock
  },

  // Transfer
  EditorialTransferOffered: {
    type: 'EditorialTransferOffered',
    label: 'Editorial Transfer Offered',
    icon: faFileCircleExclamation
  },
  TransferOfferAccepted: {
    type: 'TransferOfferAccepted',
    label: 'Transfer Offer Accepted',
    icon: faFileCircleExclamation
  },
  TransferDeclined: {
    type: 'TransferDeclined',
    label: 'Transfer Declined',
    icon: faFileCircleExclamation
  },

  // Withdrawal
  AuthorSubmissionWithdrawalRequested: {
    type: 'AuthorSubmissionWithdrawalRequested',
    label: 'Withdrawal Requested by Author',
    icon: faFileCircleExclamation
  },
  AuthorSubmissionWithdrawalRequestRevoked: {
    type: 'AuthorSubmissionWithdrawalRequestRevoked',
    label: 'Withdrawal Request Reverted by Author',
    icon: faFileCircleExclamation
  },
  EditorialSubmissionWithdrawn: {
    type: 'EditorialSubmissionWithdrawn',
    label: 'Submission Withdrawn',
    icon: faFileCircleExclamation
  },
  EditorialSubmissionWithdrawalAccepted: {
    type: 'EditorialSubmissionWithdrawalAccepted',
    label: 'Withdrawal Request Accepted',
    icon: faFileCircleExclamation
  },
  AcceptedForFormalAcceptance: {
    type: 'AcceptedForFormalAcceptance',
    label: 'Proceed to Formal Acceptance',
    icon: faFileCircleExclamation
  },

  // Flags and Tags
  SubmissionTagsReplaced: {
    type: 'SubmissionTagsReplaced',
    label: 'Submission Tags',
    icon: faFilePen
  },

  // Author Resubmit submission
  AuthorSubmissionResubmitted: {
    type: 'AuthorSubmissionResubmitted',
    label: 'Author Resubmit Submission',
    icon: faFilePen
  },

  AuthorSubmissionEdited: {
    type: 'AuthorSubmissionEdited',
    label: 'Submission Edited By Author',
    icon: faFilePen
  },

  EarlyAssessmentRequestDecision: {
    type: 'EarlyAssessmentRequestDecision',
    label: 'Early Assessment Request Decision',
    icon: faFileCircleExclamation
  },
  EarlyAssessmentRequested: {
    type: 'EarlyAssessmentRequested',
    label: 'Early Assessment Requested',
    icon: faFileCircleExclamation
  },

  EarlyAssessmentSubmitted: {
    type: 'EarlyAssessmentSubmitted',
    label: 'Early Assessment Submitted',
    icon: faFileCircleExclamation
  },

  AdjudicationRequested: {
    type: 'AdjudicationRequested',
    label: 'Adjudication Requested',
    icon: faFileCircleExclamation
  },
  AdjudicationRequestDecision: {
    type: 'AdjudicationRequestDecision',
    label: 'Adjudication Request Decision',
    icon: faFileCircleExclamation
  },
  AdjudicationSubmitted: {
    type: 'AdjudicationSubmitted',
    label: 'Adjudication Submitted',
    icon: faFileCircleExclamation
  },
  EditorialCopyrightRequested: {
    type: 'EditorialCopyrightRequested',
    label: 'Editor Requested Copyright Form',
    icon: faFilePen
  },
  EditorialICMJERequested: {
    type: 'EditorialICMJERequested',
    label: 'Editor Requested Forms And Disclosures',
    icon: faFilePen
  },
  VendorExport: {
    type: 'VendorExport',
    label: 'Vendor Export',
    icon: faFileArrowUp
  },
  EditorialCopyrightUploaded: {
    type: 'EditorialCopyrightUploaded',
    label: 'Copyright Form Uploaded By Editor',
    icon: faFilePen
  },
  EditorialICMJEUploaded: {
    type: 'EditorialICMJEUploaded',
    label: 'Forms and Disclosures Uploaded By Editor',
    icon: faFilePen
  },

  AuthorCopyrightSubmitted: {
    type: 'AuthorCopyrightSubmitted',
    label: 'Copyright Form Submitted By Author',
    icon: faFilePen
  },

  // Immediate Revision
  EditorialImmediateRevisionRequested: {
    type: 'EditorialImmediateRevisionRequested',
    label: 'Immediate Revision',
    icon: faFileCircleExclamation
  },

  CoReviewerInvited: {
    type: 'CoReviewerInvited',
    label: 'Co-Reviewer Invited',
    icon: faFileCircleExclamation
  },

  SubmissionDraftCreated: {
    type: 'SubmissionDraftCreated',
    label: 'Submission draft created',
    icon: faFilePen
  },
  SubmissionAccessed: {
    type: 'SubmissionAccessed',
    label: 'Submission accessed',
    icon: faFilePen
  },
  ReviewDeadlineExtensionDenied: {
    type: 'ReviewDeadlineExtensionDenied',
    label: 'Review Deadline Extension Request Declined',
    icon: faClock
  },
  SubmissionDeadlineExtensionDenied: {
    type: 'SubmissionDeadlineExtensionDenied',
    label: 'Submission Deadline Extension Request Declined',
    icon: faClock
  },
  ExportPreprodSubmission: {
    type: 'ExportPreprodSubmission',
    label: 'Export Preprod Submission',
    icon: faFilePen
  },
  AdhocEmailSent: {
    type: 'AdhocEmailSent',
    label: 'Ad hoc Email',
    icon: faEnvelope
  },
  PeerReviewInterrupted: {
    type: 'PeerReviewInterrupted',
    label: 'Peer Review Round Interrupted',
    icon: faFileCircleExclamation
  },
  ArticleTypeChanged: {
    type: 'ArticleTypeChanged',
    label: 'Submission Manuscript Type Edited',
    icon: faFilePen
  },
  AuthorCopyrightUploaded: {
    type: 'AuthorCopyrightUploaded',
    label: 'Copyright Form Uploaded By Author',
    icon: faFilePen
  },
  AuthorICMJEUploaded: {
    type: 'AuthorICMJEUploaded',
    label: 'Forms and Disclosures Uploaded By Author',
    icon: faFilePen
  },
  DecisionReverted: {
    type: 'DecisionReverted',
    label: 'Decision Reversal',
    icon: faFileCircleExclamation
  },
  AuthorCopyrightReset: {
    type: 'AuthorCopyrightReset',
    label: 'Copyright Form Deleted',
    icon: faFilePen
  },
  COCEditorAssigned: {
    type: 'COCEditorAssigned',
    label: 'COC Editor Assigned',
    icon: faFilePen
  },
  COCRoleUnassigned: {
    type: 'COCRoleUnassigned',
    label: 'COC Role Unassigned',
    icon: faFilePen
  },
  COCRecommendationSubmitted: {
    type: 'COCRecommendationSubmitted',
    label: 'COC Recommendation Submitted',
    icon: faFilePen
  },
  SubmissionAssignedCOC: {
    type: 'SubmissionAssignedCOC',
    label: 'Submission Assigned COC',
    icon: faFilePen
  },
  COCRecommendationModerated: {
    type: 'COCRecommendationModerated',
    label: 'COC Recommendation Moderated',
    icon: faFilePen
  },
  ContactJournal: {
    type: 'ContactJournal',
    label: 'Contact Journal',
    icon: faEnvelope
  }
} as const;

export type ACTIVITY_TYPE_VALUES =
  (typeof ACTIVITY_TYPE_INFO)[keyof typeof ACTIVITY_TYPE_INFO]['type'];

type ActivityTypeMap = {
  [K in keyof typeof ACTIVITY_TYPE_INFO]: (typeof ACTIVITY_TYPE_INFO)[K]['type'];
};

// Define ACTIVITY_TYPE with proper typing
export const ACTIVITY_TYPE = Object.fromEntries(
  Object.entries(ACTIVITY_TYPE_INFO).map(([key, value]) => [key, value.type])
) as ActivityTypeMap;

//Role-wise events activity-log
export type RoleActivityConfig = {
  allowedEventTypes: 'ALL' | readonly string[];
};

export const ROLE_WISE_ACTIVITY_LOGS: Partial<
  Record<IRoleType, RoleActivityConfig>
> = {
  editorial: {
    allowedEventTypes: 'ALL'
  },
  author: {
    allowedEventTypes: [
      'SubmissionDraftCreated',
      'SubmissionCreated',
      'SubmissionCloned',
      'SubmissionEdited',
      'ArticleTypeChanged',
      'AuthorICMJEUploaded',
      'EditorialICMJEUploaded',
      'EditorialICMJERequested',
      'AuthorCopyrightUploaded',
      'AuthorCopyrightReset',
      'EditorialChangeRequestSubmitted',
      'AuthorChangeRequestCompleted',
      'EditorialImmediateRevisionRequested',
      'RevisionRequested',
      'SubmissionDeadlineExtensionRequested',
      'SubmissionDeadlineExtended',
      'SubmissionDeadlineExtensionDenied',
      'SubmissionDeadlinePassed',
      'RevisionSubmitted',
      'AuthorSubmissionEdited',
      'AuthorSubmissionWithdrawalRequested',
      'AuthorSubmissionWithdrawalRequestRevoked',
      'AuthorSubmissionResubmitted',
      'EditorialSubmissionDecision',
      'EditorialSubmissionWithdrawn',
      'EditorialSubmissionWithdrawalAccepted',
      'AcceptedForFormalAcceptance',
      'EditorialSubmissionUnsubmitted',
      'EditorialSubmissionRejected',
      'ContactJournal',
      'AdhocEmailSent'
    ]
  },
  reviewer: {
    allowedEventTypes: [
      'ReviewRequested',
      'CoReviewerInvited',
      'ReviewReminderTriggered',
      'PeerReviewInterrupted',
      'EditorialInvitedReviewInvitationWithdrawn',
      'EditorialAcceptedReviewInvitationWithdrawn',
      'DeclinedReviewInvitationResent',
      'ExpiredReviewInvitationResent',
      'WithdrawnReviewInvitationResent',
      'ReviewDeadlineExtensionRequested',
      'ReviewDeadlineExtended',
      'ReviewDeadlineExtensionDenied',
      'ReviewDeadlinePassed',
      'ReviewInRevisionRequested',
      'RevisionReviewRequested',
      'FirstAssessmentSubmitted',
      'PeerReviewDecision',
      'EditorialAwaitingRevisionReviewInvitationWithdrawn',
      'EditorialRevisionInvitedReviewInvitationWithdrawn',
      'EditorialRevisionAssessmentReviewInvitationWithdrawn',
      'RevisionAssessmentSubmitted',
      'RescindedFirstAssessment',
      'ReviewRated',
      'ContactJournal',
      'AdhocEmailSent'
    ]
  }
};
